import { ErrorToast } from '../adminComponents/Toasts/Toasts';
import { handleError } from './api';
import { captureError } from './errorCapture';

export { setErrorCapture } from './errorCapture';

export type ReportErrorOptions = {
  /**
   * Skip the user-facing toast. Use for background failures the user shouldn't
   * see (e.g. silent retries, polling failures).
   */
  silent?: boolean;
  /**
   * Short label (e.g. "createOrder", "fetchDeliveryDetails") used as the
   * prefix when logging to console and attached as breadcrumb data in Sentry.
   * Helps disambiguate stack traces from minified bundles.
   */
  context?: string;
  /**
   * Fallback toast message when `handleError` can't extract a string from the
   * error (e.g. the API returned a non-standard shape). Defaults to the
   * generic message from `handleError`.
   */
  fallbackMessage?: string;
};

/**
 * Centralized error reporter.
 *
 * Replaces the scattered three-style pattern across the codebase:
 *   ErrorToast(handleError(error));  // old — silent in Sentry
 *   console.log(error);              // old — wrong level, no UI feedback
 *   // empty catch — invisible failure
 *
 * In one call this:
 *
 *   1. Extracts a human-readable message via `handleError` (axios → message,
 *      Error → message, fallback string).
 *   2. Shows an error toast (skippable via `silent: true`). `ErrorToast`
 *      already filters out "Unauthorized" responses so refresh-token failures
 *      don't spam the user.
 *   3. Logs to `console.error` so the original error object (with stack) is
 *      visible in dev tools.
 *   4. Forwards to `Sentry.captureException` if the host app has initialized
 *      Sentry. No-op otherwise — safe to call from any app in this monorepo.
 *
 * @example
 * try {
 *   await createOrder(payload);
 * } catch (error) {
 *   reportError(error, { context: 'createOrder' });
 * }
 */
export function reportError(
  error: unknown,
  options: ReportErrorOptions = {}
): void {
  const { silent = false, context, fallbackMessage } = options;

  const message = handleError(error) || fallbackMessage;
  const label = context ? `[${context}]` : '[error]';

  // Always log with the original object — preserves stack in devtools.
  console.error(label, error);

  // Toast unless explicitly silenced.
  if (!silent && typeof message === 'string' && message.length > 0) {
    ErrorToast(message);
  }

  // Best-effort external capture (e.g. Sentry); no-op if not wired up
  // via `setErrorCapture`.
  captureError(error);
}
